import type {KeyboardEvent, MouseEventHandler, RefObject} from 'react';
import type {ContainerQueryCondition} from '@Hooks/useContainerQuery';

export enum EventKeys {
  ENTER_KEY = 'Enter',
  SPACE_KEY = ' ',
}

const KEYBOARD_ACTIVATION_KEYS: ReadonlySet<string> = new Set([EventKeys.ENTER_KEY, EventKeys.SPACE_KEY]);

export enum MouseButton {
  PRIMARY = 0,
  MIDDLE = 1,
  SECONDARY = 2,
}

export enum FLEX_DIRECTION {
  COLUMN = 'column',
  ROW = 'row',
}

export const TABLET_SCREEN_BREAKPOINT = 768;
export const LARGE_MOBILE_SCREEN_BREAKPOINT = 550;
// Dropdown compares with `<`, the mobile layout applies at `<=`.
export const LARGE_MOBILE_DROPDOWN_MODAL_THRESHOLD = LARGE_MOBILE_SCREEN_BREAKPOINT + 1;
export const MOBILE_SCREEN_BREAKPOINT = 480;

const TEXT_ENTRY_INPUT_TYPES = ['text', 'number', 'search', 'email', 'url', 'password', 'tel'];

/**
 * Whether keyboard input currently goes to a text-entry element (a typeable input, a textarea,
 * or a contenteditable). Callers use this to avoid stealing focus from a field the user is
 * typing in; non-typing controls (buttons, checkboxes, sliders) intentionally return false.
 */
/** Suppresses text selection for the duration of a pointer drag (a resize grip, a timeline scrub); pair with `restoreBodyTextSelection`. */
export const disableBodyTextSelection = (): void => {
  document.body.style.userSelect = 'none';
};

export const restoreBodyTextSelection = (): void => {
  document.body.style.userSelect = '';
};

export const isTextEntryElementFocused = (): boolean => {
  return isTextEntryElement(document.activeElement);
};

/**
 * Whether the given element takes typed input. Prefer this over the focused-element check when handling
 * a key event: a field may blur itself on Enter before the event bubbles up, so `event.target` is the
 * reliable record of where the key went.
 */
export const isTextEntryElement = (element: EventTarget | null): boolean => {
  if (element instanceof HTMLInputElement) {
    return TEXT_ENTRY_INPUT_TYPES.includes(element.type);
  }
  if (element instanceof HTMLTextAreaElement) {
    return true;
  }
  return element instanceof HTMLElement && element.isContentEditable;
};

export const isContainerOverflowing = (container: HTMLElement): boolean => {
  return container.scrollHeight - container.clientHeight > 0;
};

export const scrollToTopOfElement = (element: HTMLElement, onScrolled: VoidFunction): void => {
  element.scrollTo({top: 0, behavior: 'smooth'});
  const checkIfScrolled = (): void => {
    if (element.scrollTop <= 0) {
      onScrolled();
    } else {
      requestAnimationFrame(checkIfScrolled);
    }
  };

  requestAnimationFrame(checkIfScrolled);
};

/**
 * `occludedBottomHeight` trims the container's viewport from the bottom for chrome that overlays it
 * (e.g. the editor's timeline bar), so covered pixels don't count as visible.
 */
export const getVisibleHeightOfElement = (element: HTMLElement, container: HTMLElement, occludedBottomHeight = 0): number => {
  const {scrollTop} = container;
  const scrollBottom = scrollTop + container.clientHeight - occludedBottomHeight;

  const elementRect = element.getBoundingClientRect();
  const elementTop = elementRect.top - container.getBoundingClientRect().top + scrollTop;
  const elementBottom = elementTop + elementRect.height;
  const visibleTop = elementTop < scrollTop ? scrollTop : elementTop;
  const visibleBottom = elementBottom > scrollBottom ? scrollBottom : elementBottom;

  return visibleBottom - visibleTop;
};

export function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  if (!el) {
    return null;
  }

  let parent: HTMLElement | null = el.parentElement;

  while (parent && parent !== document.body) {
    const style = window.getComputedStyle(parent);
    const overflowY = style.overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') && parent.scrollHeight > parent.clientHeight) {
      return parent;
    }

    parent = parent.parentElement;
  }

  return null;
}

/**
 * Calculates the max visible height of each child element inside the scrollabeContainer and returns the one with the max height.
 * @param childElements
 * @param scrollabeContainer
 */
export const getChildElementWithMaxVisibleHeight = (childElements: HTMLElement[], scrollabeContainer: HTMLElement, occludedBottomHeight = 0): HTMLElement | undefined => {
  let visibleElementHeight = 0;
  let maxVisibleElementHeight = 0;
  let maxVisibleElementHeightIndex = 0;

  for (let i = 0; i < childElements.length; i++) {
    // Gets the amount of pixels currently visible within the scrollabeContainer
    visibleElementHeight = getVisibleHeightOfElement(childElements[i], scrollabeContainer, occludedBottomHeight);

    if (visibleElementHeight > maxVisibleElementHeight) {
      maxVisibleElementHeightIndex = i;
      maxVisibleElementHeight = visibleElementHeight;
    } else if (visibleElementHeight < 0) {
      break;
    }
  }
  return childElements[maxVisibleElementHeightIndex];
};

export const isTabletScreenWidth = (): boolean => {
  return window.innerWidth <= TABLET_SCREEN_BREAKPOINT;
};

export const isLargeMobileScreenWidth = (): boolean => {
  return window.innerWidth <= LARGE_MOBILE_SCREEN_BREAKPOINT;
};

export const isMobileScreenWidth = (): boolean => {
  return window.innerWidth <= MOBILE_SCREEN_BREAKPOINT;
};

export const isTextElement = (element: Element): boolean => {
  const tagName = element.tagName.toLowerCase();
  if (tagName === 'textarea') return true;
  if (tagName !== 'input') return false;
  let type = element.getAttribute('type');
  if (type) {
    type = type.toLowerCase();
    // if any of these input types is not supported by a browser, it will behave as input type text.
    const inputTypes = ['text', 'password', 'number', 'email', 'tel', 'url', 'search', 'date', 'datetime', 'datetime-local', 'time', 'month', 'week'];
    return inputTypes.indexOf(type) >= 0;
  }
  return false;
};

export const areChecksTrueForContainerQueryBreakpoint = (containerQueryBreakpoint: ContainerQueryCondition, domElementWidth: number, domElementHeight: number): boolean => {
  if (containerQueryBreakpoint.maxHeight && !(domElementHeight <= containerQueryBreakpoint.maxHeight)) {
    return false;
  }
  if (containerQueryBreakpoint.maxWidth && !(domElementWidth <= containerQueryBreakpoint.maxWidth)) {
    return false;
  }
  if (containerQueryBreakpoint.minHeight && !(domElementHeight >= containerQueryBreakpoint.minHeight)) {
    return false;
  }

  if (containerQueryBreakpoint.minWidth && !(domElementWidth >= containerQueryBreakpoint.minWidth)) {
    return false;
  }

  return true;
};

/**
 * checks whether an element is visible completely in the viewport only horizontally
 * @param element
 */
export const isHorizontallyInViewport = (element: HTMLElement): boolean => {
  const boundingBox = element.getBoundingClientRect();
  return boundingBox.left >= 0 && boundingBox.right <= (document.documentElement.clientWidth || document.body.clientWidth || window.innerWidth);
};

export const isOverflowingViewportFromTop = (element: HTMLElement): boolean => {
  return element.getBoundingClientRect().top < 0;
};

export const addClickOutsideListener = (elementQuerySelector: string, callback: () => void | Promise<void>): VoidFunction => {
  const handleClickOutside = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    const element = document.querySelectorAll(elementQuerySelector)[0];

    if (element && !element.contains(target)) {
      void callback();
    }
  };

  document.addEventListener('click', handleClickOutside, {capture: true});

  return (): void => {
    document.removeEventListener('click', handleClickOutside, {capture: true});
  };
};

export const scrollToBottom = (container: HTMLDivElement | null, behavior: ScrollBehavior = 'smooth'): void => {
	if (!container) {
		return;
	}

	container.scrollTo({top: container.scrollHeight, behavior: behavior});
};

export const scrollToBottomNextFrame = (containerRef: RefObject<HTMLDivElement | null>, behavior: ScrollBehavior = 'smooth'): void => {
	requestAnimationFrame(() => {
		scrollToBottom(containerRef.current, behavior);
	});
};

export const autoResizeTextarea = (
  el: HTMLTextAreaElement,
  minHeight = 38,
  maxHeight = 188
): void => {
  el.rows = 1;
  el.style.minHeight = '0';
  el.style.maxHeight = `${maxHeight}px`;
  el.style.height = 'auto';
  const contentHeight = el.scrollHeight;
  const lineHeight = parseInt(getComputedStyle(el).lineHeight, 10) || 20;
  const minRows = Math.ceil(minHeight / lineHeight);
  el.rows = Math.max(minRows, Math.min(Math.ceil(contentHeight / lineHeight), Math.ceil(maxHeight / lineHeight)));
  const height = Math.min(Math.max(contentHeight, minHeight), maxHeight);
  el.style.height = `${height}px`;
  el.style.minHeight = `${minHeight}px`;
  el.style.overflowY = contentHeight > maxHeight ? 'auto' : 'hidden';
};
/** True when a wheel event's magnitude looks like a discrete mouse-wheel notch rather than trackpad inertia. */
export function isMouseWheelEvent(e: WheelEvent): boolean {
  const absDelta = Math.abs(e.deltaY);
  return absDelta >= 10;
}

export const clickOnKeyboardActivation = (event: KeyboardEvent<HTMLElement>): void => {
  if (!KEYBOARD_ACTIVATION_KEYS.has(event.key)) {
    return;
  }

  event.preventDefault();
  event.currentTarget.click();
};

export const withoutNativeInputClicks = (handler: VoidFunction): MouseEventHandler<HTMLElement> => {
  return (event): void => {
    if (event.target instanceof HTMLInputElement) {
      return;
    }

    handler();
  };
};

/** Reads a JSON payload the server serialised onto an element's data attribute. Null if unreadable. */
export function readJsonDataAttribute<T>(element: HTMLElement, attributeName: string): Partial<T> | null {
  try {
    // A missing attribute parses as '' and throws, so it lands on the same branch as bad JSON.
    return JSON.parse(element.getAttribute(attributeName) ?? '') as Partial<T>;
  } catch {
    return null;
  }
}

const ELLIPSIS = '…';
/** Sub-pixel rounding can make text that fits measure a fraction over its lines. */
const LINE_FIT_TOLERANCE_PX = 1;

/**
 * The longest start of `text` that fits in `maxLines` of `element`, ending in an ellipsis when cut. Words still wrap
 * normally, so only the last line is cut mid-word. Measures on a hidden copy so React's own text node is untouched;
 * an element with no width yet (still hidden) gets the full text back, to be re-measured once it is laid out.
 */
export const getTextFittingLines = (element: HTMLElement, text: string, maxLines: number): string => {
  const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight);
  if (Number.isNaN(lineHeight) || !element.parentElement || element.clientWidth === 0) {
    return text;
  }
  const maxHeight = lineHeight * maxLines + LINE_FIT_TOLERANCE_PX;
  const probe = createTextProbe(element);
  element.parentElement.appendChild(probe);
  try {
    return findLongestFittingText(text, (candidate: string): boolean => {
      probe.textContent = candidate;
      return probe.scrollHeight <= maxHeight;
    });
  } finally {
    probe.remove();
  }
};

/** Binary search over the cut point: the full text when it fits, otherwise the longest prefix that fits with an ellipsis. */
const findLongestFittingText = (text: string, fits: (candidate: string) => boolean): string => {
  if (fits(text)) {
    return text;
  }
  let shortest = 0;
  let longest = text.length - 1;
  while (shortest < longest) {
    const middle = Math.ceil((shortest + longest) / 2);
    if (fits(`${text.slice(0, middle).trimEnd()}${ELLIPSIS}`)) {
      shortest = middle;
    } else {
      longest = middle - 1;
    }
  }
  return `${text.slice(0, shortest).trimEnd()}${ELLIPSIS}`;
};

/** An invisible, unclamped copy of the element's box and font, for measuring how much text it can hold. */
const createTextProbe = (element: HTMLElement): HTMLElement => {
  const probe = document.createElement(element.tagName.toLowerCase());
  probe.className = element.className;
  Object.assign(probe.style, {
    display: 'block',
    left: '0',
    overflow: 'visible',
    position: 'absolute',
    top: '0',
    visibility: 'hidden',
    webkitLineClamp: 'none',
    width: `${element.clientWidth}px`,
  });
  probe.setAttribute('aria-hidden', 'true');
  return probe;
};
